import React from "react";
import { Play, Pause, SkipBack, SkipForward, SpeakerHigh, SpeakerSlash, MusicNote } from "@phosphor-icons/react";
import { fileUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";

function fmt(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function NowPlayingBar() {
  const {
    currentTrack, currentIndex, isPlaying, isMuted, currentTime, duration,
    play, pause, next, prev, seek, toggleMute,
  } = usePlayer();

  if (!currentTrack) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] glass border-t border-neon-cyan/20"
      data-testid="now-playing-bar"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center gap-4">
        {/* Artwork + meta */}
        <div className="flex items-center gap-3 min-w-0 w-1/4">
          <div className="w-12 h-12 rounded-md overflow-hidden bg-ink-800 border border-ink-600 shrink-0 relative">
            {currentTrack.image_path ? (
              <img
                src={fileUrl(currentTrack.image_path)}
                alt=""
                className={`w-full h-full object-cover ${isPlaying ? "animate-pulse-glow" : ""}`}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-ink-600">
                <MusicNote size={20} />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-mono tracking-widest text-neon-cyan uppercase" data-testid="now-playing-rank">
              #{String(currentTrack.rank).padStart(2, "0")} · Now playing
            </p>
            <p className="text-sm text-white font-semibold truncate" data-testid="now-playing-title">
              {currentTrack.song_title}
            </p>
            <p className="text-xs text-zinc-500 truncate" data-testid="now-playing-artist">
              {currentTrack.artist_name}
            </p>
          </div>
        </div>

        {/* Transport */}
        <div className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              aria-label="Previous"
              className="w-9 h-9 rounded-md text-zinc-300 hover:text-neon-cyan transition-colors flex items-center justify-center"
              data-testid="np-prev"
            >
              <SkipBack size={18} weight="fill" />
            </button>
            <button
              onClick={() => (isPlaying ? pause() : play())}
              aria-label={isPlaying ? "Pause" : "Play"}
              className="w-11 h-11 rounded-full bg-neon-cyan text-black flex items-center justify-center hover:bg-neon-cyanHover transition-colors shadow-[0_0_24px_rgba(255,138,60,0.45)]"
              data-testid="np-toggle"
            >
              {isPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
            </button>
            <button
              onClick={next}
              aria-label="Next"
              className="w-9 h-9 rounded-md text-zinc-300 hover:text-neon-cyan transition-colors flex items-center justify-center"
              data-testid="np-next"
            >
              <SkipForward size={18} weight="fill" />
            </button>
          </div>
          <div className="flex items-center gap-2 w-full max-w-xl">
            <span className="font-mono text-[10px] text-zinc-500 tabular-nums w-9 text-right">{fmt(currentTime)}</span>
            <input
              type="range"
              className="progress flex-1"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Seek"
              data-testid="np-seek"
            />
            <span className="font-mono text-[10px] text-zinc-500 tabular-nums w-9">{fmt(duration)}</span>
          </div>
        </div>

        {/* Volume / mute */}
        <div className="w-1/4 flex items-center justify-end gap-2">
          {isMuted && (
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-neon-cyan animate-pulse">
              Tap to unmute
            </span>
          )}
          <button
            onClick={toggleMute}
            aria-label={isMuted ? "Unmute" : "Mute"}
            className="w-9 h-9 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/60 transition-colors flex items-center justify-center"
            data-testid="np-mute"
          >
            {isMuted ? <SpeakerSlash size={16} /> : <SpeakerHigh size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
